import axios from "axios";
import { useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  ArrowLeft,
  Envelope,
  CalendarCheck,
  PersonCheck,
  CalendarPlus,
  ShieldCheck,
  Lock,
  PencilSquare,
  Clock,
  ChevronRight,
} from "react-bootstrap-icons";


import AppointmentDetailsModal from "../../modals/AppointmentDetailsModal";
import PrivacyPinModal from "../../modals/PrivacyPinModal"

import "./ClientPage.css";
import LoadingSpinner from "../../components/LoadingSpinner";
import { ApiConnectionString } from "../../constants/ApiConntection";
import { useAuthContext } from "../../hooks/useAuthContext";

export default function ClientPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const [client, setClient] = useState(null);
  const [appointments, setAppointments] = useState([]);

  const [showAppointmentDetailsModal, setShowAppointmentDetailsModal] =
    useState(false);

  const [selectedAppointment, setSelectedAppointment] =
    useState(null);

  const [showPrivacyPinModal, setShowPrivacyPinModal] =
    useState(false);

  const [privacyUnlocked, setPrivacyUnlocked] =
    useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState("");

  const getPrivacyStatus = async () => {
    if (!user?.token) {
      return;
    }

    try {
      const response = await axios.get(
        `${ApiConnectionString}/api/privacy-pin/status`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const data = response.data || {};

      setPrivacyUnlocked(
        data.unlocked ??
          data.Unlocked ??
          data.isUnlocked ??
          data.IsUnlocked ??
          data.privacyUnlocked ??
          data.PrivacyUnlocked ??
          false
      );
    } catch (error) {
      console.error(
        "Failed to load privacy PIN status:",
        error
      );

      setPrivacyUnlocked(false);
    }
  };

  const getClientData = async () => {
    setIsLoading(true);
    setErr("");

    try {
      const clientResponse = await axios.get(
        `${ApiConnectionString}/api/client/${id}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      setClient(clientResponse.data);

      const appointmentsResponse = await axios.get(
        `${ApiConnectionString}/api/Appointments/Client/${id}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      setAppointments(appointmentsResponse.data);
    } catch (error) {
      console.error(error);
      setErr("Failed to load client.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.token && id) {
      getClientData();
      getPrivacyStatus();
    }
  }, [user?.token, id]);

  const handleEditClient = (event) => {
    if (privacyUnlocked) {
      return;
    }

    event.preventDefault();
    setShowPrivacyPinModal(true);
  };

  const handlePrivacyUnlocked = async () => {
    setPrivacyUnlocked(true);
    setShowPrivacyPinModal(false);

    navigate(`/client/${id}/edit`);
  };

  const openAppointmentDetailsModal = (appointment) => {
    setSelectedAppointment(appointment);
    setShowAppointmentDetailsModal(true);
  };

  const closeAppointmentDetailsModal = () => {
    setShowAppointmentDetailsModal(false);
    setSelectedAppointment(null);
  };

  const handleAppointmentUpdated = async () => {
    closeAppointmentDetailsModal();
    await getClientData();
  };

  const handleAppointmentDeleted = async () => {
    closeAppointmentDetailsModal();
    await getClientData();
  };

  const getClientValue = (lower, upper) => {
    return client?.[lower] || client?.[upper] || "";
  };

  const getAppointmentValue = (
    appointment,
    lower,
    upper
  ) => {
    return (
      appointment?.[lower] ||
      appointment?.[upper] ||
      ""
    );
  };

  const firstName =
    getClientValue("firstName", "FirstName");

  const lastName =
    getClientValue("lastName", "LastName");

  const email =
    getClientValue("email", "Email");

  const emailHidden =
    client?.emailHidden ??
    client?.EmailHidden ??
    false;

  const created =
    getClientValue("created", "Created");

  const isActive =
    client?.isActive ??
    client?.IsActive ??
    true;

  const clientPortalEnabled =
    client?.clientPortalEnabled ??
    client?.ClientPortalEnabled ??
    false;

  const emailVerified =
    client?.emailVerified ??
    client?.EmailVerified ??
    false;

  const canSelfBookAppointments =
    client?.canSelfBookAppointments ??
    client?.CanSelfBookAppointments ??
    false;

  const fullName =
    `${firstName} ${lastName}`.trim();

  const initials =
    `${firstName.charAt(0)}${lastName.charAt(0)}`
      .toUpperCase() || "?";

  const getPortalClass = () => {
    if (!clientPortalEnabled) {
      return "self-booking-off";
    }

    if (!emailVerified) {
      return "self-booking-warning";
    }

    return "self-booking-ready";
  };

  const getPortalBadgeText = () => {
    if (!clientPortalEnabled) {
      return "Portal off";
    }

    if (!emailVerified) {
      return "Invite pending";
    }

    return "Portal active";
  };

  const getSelfBookingClass = () => {
    if (
      !clientPortalEnabled ||
      !canSelfBookAppointments
    ) {
      return "self-booking-off";
    }

    if (!emailVerified) {
      return "self-booking-warning";
    }

    return "self-booking-ready";
  };

  const getSelfBookingBadgeText = () => {
    if (!clientPortalEnabled) {
      return "Unavailable";
    }

    if (!canSelfBookAppointments) {
      return "Off";
    }

    if (!emailVerified) {
      return "Waiting verification";
    }

    return "On";
  };

  const getAccessMessage = () => {
    if (!clientPortalEnabled) {
      return "Client portal is disabled.";
    }

    if (!emailVerified) {
      return "Portal invite sent. Waiting for the client to verify their email.";
    }

    if (canSelfBookAppointments) {
      return "Portal active. Client can view appointments and self-book.";
    }

    return "Portal active. Client can view appointments.";
  };

  const sortedAppointments =
    [...appointments].sort((a, b) => {
      const bStart =
        getAppointmentValue(
          b,
          "start",
          "Start"
        );

      const aStart =
        getAppointmentValue(
          a,
          "start",
          "Start"
        );

      return (
        new Date(bStart) -
        new Date(aStart)
      );
    });

  const upcomingAppointment =
    [...appointments]
      .filter((appointment) => {
        const start =
          getAppointmentValue(
            appointment,
            "start",
            "Start"
          );

        return new Date(start) >= new Date();
      })
      .sort((a, b) => {
        const aStart =
          getAppointmentValue(
            a,
            "start",
            "Start"
          );

        const bStart =
          getAppointmentValue(
            b,
            "start",
            "Start"
          );

        return (
          new Date(aStart) -
          new Date(bStart)
        );
      })[0];

  const recentAppointments =
    sortedAppointments
      .filter((appointment) => {
        const start =
          getAppointmentValue(
            appointment,
            "start",
            "Start"
          );

        return new Date(start) < new Date();
      })
      .slice(0, 5);

  const formatDate = (date) => {
    if (!date) {
      return "Unknown";
    }

    return new Date(date).toLocaleDateString(
      "en-GB",
      {
        day: "numeric",
        month: "long",
        year: "numeric",
      }
    );
  };

  const formatTime = (date) => {
    if (!date) {
      return "";
    }

    return new Date(date).toLocaleTimeString(
      [],
      {
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  };

  const getAppointmentDuration = (
    appointment
  ) => {
    const sessionLength =
      appointment?.sessionLengthMinutes ??
      appointment?.SessionLengthMinutes ??
      null;

    if (sessionLength) {
      return Number(sessionLength);
    }

    const start =
      getAppointmentValue(
        appointment,
        "start",
        "Start"
      );

    const finish =
      getAppointmentValue(
        appointment,
        "finish",
        "Finish"
      );

    if (!start || !finish) {
      return null;
    }

    return Math.round(
      (
        new Date(finish).getTime() -
        new Date(start).getTime()
      ) / 60000
    );
  };

  const getAppointmentName = (
    appointment
  ) => {
    return (
      appointment?.name ??
      appointment?.Name ??
      "Therapy session"
    );
  };

  if (isLoading) {
    return (
      <div className="client-loading">
        <LoadingSpinner />
      </div>
    );
  }

  if (err) {
    return (
      <div className="client-page">
        <Link
          to="/clients"
          className="client-back-link"
        >
          <ArrowLeft />
          Back to clients
        </Link>

        <div className="alert alert-danger">
          {err}
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="client-page">
        <Link
          to="/clients"
          className="client-back-link"
        >
          <ArrowLeft />
          Back to clients
        </Link>

        <div className="client-empty">
          <h2>Client not found</h2>

          <p>
            This client could not be found.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PrivacyPinModal
        show={showPrivacyPinModal}
        token={user?.token}
        onClose={() =>
          setShowPrivacyPinModal(false)
        }
        onUnlocked={handlePrivacyUnlocked}
      />

      <AppointmentDetailsModal
        show={showAppointmentDetailsModal}
        appointment={selectedAppointment}
        onClose={
          closeAppointmentDetailsModal
        }
        onUpdated={
          handleAppointmentUpdated
        }
        onDeleted={
          handleAppointmentDeleted
        }
      />

      <div className="client-page">
        <Link
          to="/clients"
          className="client-back-link"
        >
          <ArrowLeft />
          Back to clients
        </Link>

        {!isActive && (
          <div className="client-archived-alert">
            <div>
              <strong>
                This client is archived
              </strong>

              <span>
                They are hidden from your active client
                list. Restore the client before booking
                a new appointment.
              </span>
            </div>

            <Link
              to={`/client/${id}/edit`}
              className="client-archived-restore-btn"
              onClick={handleEditClient}
            >
              Edit and restore client
            </Link>
          </div>
        )}

        <header className="client-header">
          <div>
            <span>
              {isActive
                ? "Client profile"
                : "Archived client"}
            </span>

            <h1>{fullName}</h1>
          </div>

          <div className="client-header-actions">
            <Link
              to={`/client/${id}/edit`}
              className="client-secondary-btn"
              onClick={handleEditClient}
            >
              <PencilSquare />
              Edit client
            </Link>

            {isActive && (
              <Link
                to="/appointments"
                className="client-main-btn"
              >
                <CalendarPlus />
                Book appointment
              </Link>
            )}
          </div>
        </header>

        <section
          className={`client-profile-card ${
            !isActive
              ? "client-profile-card-archived"
              : ""
          }`}
        >
          <div className="client-profile-identity">
            <div className="client-avatar-wrap">
              <div className="client-avatar-large">
                {initials}
              </div>

              <span
                className={`client-avatar-status ${
                  isActive
                    ? "active"
                    : "archived"
                }`}
              ></span>
            </div>

            <div className="client-profile-details">
              <h2>{fullName}</h2>

              <div className="client-profile-detail">
                <Envelope />

                <span>
                  {emailHidden
                    ? "Email hidden"
                    : email ||
                      "No email saved"}
                </span>
              </div>

              <div className="client-profile-detail">
                <CalendarCheck />

                <span>
                  Client since{" "}
                  {formatDate(created)}
                </span>
              </div>
            </div>
          </div>

          <div className="client-profile-access">
            <div className="client-access-row">
              <Lock />

              <div>
                <span>
                  Portal status
                </span>

                <strong
                  className={`client-badge ${getPortalClass()}`}
                >
                  {getPortalBadgeText()}
                </strong>
              </div>
            </div>

            <div className="client-access-row">
              <ShieldCheck />

              <div>
                <span>
                  Self-booking
                </span>

                <strong
                  className={`client-badge ${getSelfBookingClass()}`}
                >
                  {getSelfBookingBadgeText()}
                </strong>
              </div>
            </div>
          </div>

          <div
            className={`client-profile-state ${
              isActive
                ? "active"
                : "archived"
            }`}
          >
            <PersonCheck />

            <div>
              <strong>
                {isActive
                  ? "Active client"
                  : "Archived client"}
              </strong>

              <span>
                {isActive
                  ? "Client is active and can receive appointments."
                  : "Client is currently hidden from active lists."}
              </span>
            </div>
          </div>
        </section>

        <div className="client-grid">
          <main className="client-left">
            <section className="client-card client-next-card">
              <div className="client-section-heading">
                <span className="client-card-icon">
                  <CalendarCheck />
                </span>

                <h2>
                  Next appointment
                </h2>
              </div>

              {upcomingAppointment ? (
                <div className="client-next-appointment">
                  <div className="client-next-date">
                    <span className="client-next-calendar">
                      <CalendarCheck />
                    </span>

                    <div>
                      <strong>
                        {formatDate(
                          getAppointmentValue(
                            upcomingAppointment,
                            "start",
                            "Start"
                          )
                        )}
                      </strong>

                      <span>
                        {formatTime(
                          getAppointmentValue(
                            upcomingAppointment,
                            "start",
                            "Start"
                          )
                        )}{" "}
                        –{" "}
                        {formatTime(
                          getAppointmentValue(
                            upcomingAppointment,
                            "finish",
                            "Finish"
                          )
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="client-next-meta">
                    <Clock />

                    <div>
                      <strong>
                        {getAppointmentDuration(
                          upcomingAppointment
                        )
                          ? `${getAppointmentDuration(
                              upcomingAppointment
                            )} min`
                          : "Session"}
                      </strong>

                      <span>
                        {getAppointmentName(
                          upcomingAppointment
                        )}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="client-view-details-btn"
                    onClick={() =>
                      openAppointmentDetailsModal(
                        upcomingAppointment
                      )
                    }
                  >
                    View details
                    <ChevronRight />
                  </button>
                </div>
              ) : (
                <div className="client-next-empty">
                  <span className="client-empty-calendar">
                    <CalendarCheck />
                  </span>

                  <div>
                    <strong>
                      No upcoming appointments
                    </strong>

                    <p>
                      Use the Book appointment
                      button above to schedule
                      the next session for this
                      client.
                    </p>
                  </div>
                </div>
              )}
            </section>

            <section className="client-card client-list-card">
              <div className="client-list-card-header">
                <div className="client-section-heading">
                  <span className="client-card-icon">
                    <CalendarCheck />
                  </span>

                  <h2>
                    Recent appointments
                  </h2>
                </div>

                {appointments.length > 5 && (
                  <Link
                    to="/appointments"
                    className="client-small-link"
                  >
                    View all
                    <ChevronRight />
                  </Link>
                )}
              </div>

              {recentAppointments.length ===
              0 ? (
                <p className="client-muted">
                  No previous appointments
                  yet.
                </p>
              ) : (
                <div className="client-appointment-list">
                  {recentAppointments.map(
                    (appointment) => (
                      <button
                        type="button"
                        className="client-appointment-row"
                        key={
                          appointment.id ||
                          appointment.Id
                        }
                        onClick={() =>
                          openAppointmentDetailsModal(
                            appointment
                          )
                        }
                      >
                        <span className="client-row-calendar">
                          <CalendarCheck />
                        </span>

                        <div className="client-appointment-date">
                          <strong>
                            {formatDate(
                              getAppointmentValue(
                                appointment,
                                "start",
                                "Start"
                              )
                            )}
                          </strong>

                          <span>
                            {formatTime(
                              getAppointmentValue(
                                appointment,
                                "start",
                                "Start"
                              )
                            )}{" "}
                            –{" "}
                            {formatTime(
                              getAppointmentValue(
                                appointment,
                                "finish",
                                "Finish"
                              )
                            )}
                          </span>
                        </div>

                        <div className="client-appointment-duration">
                          <Clock />

                          <div>
                            <strong>
                              {getAppointmentDuration(
                                appointment
                              )
                                ? `${getAppointmentDuration(
                                    appointment
                                  )} min`
                                : "Session"}
                            </strong>

                            <span>
                              {getAppointmentName(
                                appointment
                              )}
                            </span>
                          </div>
                        </div>

                        <span className="client-view-details-btn">
                          View details
                          <ChevronRight />
                        </span>
                      </button>
                    )
                  )}
                </div>
              )}
            </section>
          </main>

          <aside className="client-right">
            <section className="client-summary-card">
              <h2>
                Summary
              </h2>

              {!isActive && (
                <div className="client-summary-archived">
                  <strong>
                    Archived client
                  </strong>

                  <span>
                    Not shown in your active
                    client list
                  </span>
                </div>
              )}

              <div className="client-summary-item">
                <PersonCheck />

                <div>
                  <span>
                    Total sessions
                  </span>

                  <strong>
                    {appointments.length}
                  </strong>
                </div>
              </div>

              <div className="client-summary-item">
                <CalendarCheck />

                <div>
                  <span>
                    Next appointment
                  </span>

                  <strong>
                    {upcomingAppointment
                      ? `${formatDate(
                          getAppointmentValue(
                            upcomingAppointment,
                            "start",
                            "Start"
                          )
                        )}, ${formatTime(
                          getAppointmentValue(
                            upcomingAppointment,
                            "start",
                            "Start"
                          )
                        )}`
                      : "None scheduled"}
                  </strong>
                </div>
              </div>

              <div className="client-summary-item">
                <PersonCheck />

                <div>
                  <span>
                    Client status
                  </span>

                  <strong className="client-summary-status">
                    <i
                      className={
                        isActive
                          ? "active"
                          : "archived"
                      }
                    ></i>

                    {isActive
                      ? "Active client"
                      : "Archived client"}
                  </strong>
                </div>
              </div>

              <div className="client-summary-item">
                <ShieldCheck />

                <div>
                  <span>
                    Self-booking
                  </span>

                  <strong
                    className={`client-badge ${getSelfBookingClass()}`}
                  >
                    {getSelfBookingBadgeText()}
                  </strong>
                </div>
              </div>
            </section>
          </aside>
        </div>
      </div>
    </>
  );
}